/**
 * Monitor City — Quote Cart
 * localStorage-backed multi-item quote builder for the Shopify storefront.
 *
 * Public API on window.MCQuote:
 *   add(item)     — push an item ({productId, variantId, handle, title, image, price, qty, stock})
 *   remove(id)    — remove by variantId
 *   updateQty(id, qty) — set qty for a line, capped at item.stock
 *   clear()       — empty the cart
 *   get()         — return current items array
 *   count()       — return total qty across all items
 *   open()/close()/toggle() — drawer controls
 *   refreshLiveStock() — re-fetches /products/{handle}.js for each line and updates stock
 *
 * Events dispatched on document:
 *   mc:quote:changed   — fires after any add/remove/update/clear
 *   mc:quote:opened    — drawer opened
 *   mc:quote:closed    — drawer closed
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mc_quote_cart';
  // Cart TTL: clear if not touched in 24h to limit cross-user leakage on shared browsers.
  var TTL_MS = 24 * 60 * 60 * 1000;
  var DRAWER_ID = 'mc-quote-drawer';
  var BADGE_SELECTOR = '[data-mc-quote-count]';
  var ITEMS_CONTAINER = '[data-mc-quote-items]';

  // ---------- Storage (TTL-aware: 24h idle expiry) ----------
  // Storage shape: { items: [...], lastTouched: <epoch ms> }
  // Backwards-compat: if old shape (raw array) is found, migrate to new shape on next write.
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      // Old shape — bare array. Treat as fresh (no timestamp) and migrate.
      if (Array.isArray(parsed)) return parsed;
      // New shape — { items, lastTouched }. Check TTL.
      if (parsed && parsed.items) {
        if (typeof parsed.lastTouched === 'number' && Date.now() - parsed.lastTouched > TTL_MS) {
          // Expired — purge and return empty.
          try { localStorage.removeItem(STORAGE_KEY); } catch (e2) {}
          return [];
        }
        return parsed.items || [];
      }
      return [];
    } catch (e) {
      return [];
    }
  }
  function write(items) {
    try {
      var payload = { items: items, lastTouched: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) { /* quota or private mode */ }
    fire('mc:quote:changed');
    render();
  }

  // ---------- Public API ----------
  function add(item) {
    if (!item || !item.variantId) return;
    var items = read();
    var existing = items.find(function (i) { return i.variantId === item.variantId; });
    var stock = (typeof item.stock === 'number') ? item.stock : null;

    if (existing) {
      var newQty = existing.qty + (item.qty || 1);
      if (stock !== null) newQty = Math.min(newQty, stock);
      existing.qty = newQty;
      existing.stockAtAdd = stock;
    } else {
      items.push({
        productId: item.productId,
        variantId: item.variantId,
        handle: item.handle,
        title: item.title,
        variantTitle: item.variantTitle || null,
        url: item.url,
        image: item.image,
        price: item.price,
        qty: Math.max(1, Math.min(item.qty || 1, stock || 999)),
        stockAtAdd: stock,
        liveStock: stock,
        liveAvailable: true,
        addedAt: new Date().toISOString()
      });
    }
    write(items);
  }

  function remove(variantId) {
    write(read().filter(function (i) { return i.variantId !== variantId; }));
  }

  function updateQty(variantId, qty) {
    var items = read();
    var item = items.find(function (i) { return i.variantId === variantId; });
    if (!item) return;
    var max = (typeof item.liveStock === 'number') ? item.liveStock : 999;
    item.qty = Math.max(1, Math.min(qty, max));
    write(items);
  }

  function clear() {
    write([]);
  }

  function get() {
    return read();
  }

  function count() {
    return read().reduce(function (sum, i) { return sum + (i.qty || 0); }, 0);
  }

  // ---------- Live stock refresh ----------
  function refreshLiveStock() {
    var items = read();
    if (!items.length) return Promise.resolve(items);

    return Promise.all(items.map(function (item) {
      if (!item.handle) return item;
      return fetch('/products/' + item.handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (live) {
          if (!live) {
            item.liveAvailable = null;
            return item;
          }
          var v = live.variants.find(function (x) { return x.id === item.variantId; }) || live.variants[0];
          item.liveStock = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity : null;
          item.liveAvailable = !!v.available;
          // Cap qty to live stock
          if (typeof item.liveStock === 'number' && item.qty > item.liveStock) {
            item.qty = Math.max(1, item.liveStock);
          }
          return item;
        })
        .catch(function () { item.liveAvailable = null; return item; });
    })).then(function (updated) {
      write(updated);
      return updated;
    });
  }

  // ---------- Drawer controls ----------
  function open() {
    var d = document.getElementById(DRAWER_ID);
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mc-quote-open');
    refreshLiveStock();
    fire('mc:quote:opened');
  }
  function close() {
    var d = document.getElementById(DRAWER_ID);
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mc-quote-open');
    fire('mc:quote:closed');
  }
  function toggle() {
    var d = document.getElementById(DRAWER_ID);
    if (!d) return;
    d.classList.contains('is-open') ? close() : open();
  }

  // ---------- Render ----------
  function render() {
    // Update count badges
    var c = count();
    document.querySelectorAll(BADGE_SELECTOR).forEach(function (el) {
      el.textContent = c;
      el.classList.toggle('is-empty', c === 0);
    });

    // Render drawer items
    var container = document.querySelector(ITEMS_CONTAINER);
    if (!container) return;

    var items = read();
    if (!items.length) {
      container.innerHTML = '<p class="mc-quote-empty">Your quote request is empty.</p>';
      return;
    }

    container.innerHTML = items.map(function (item) {
      var stockNote = '';
      if (typeof item.liveStock === 'number') {
        stockNote = '<span class="mc-quote-stock">' +
          (item.liveStock <= 1 ? 'Last one' :
            (item.liveStock <= 3 ? 'Only ' + item.liveStock + ' left' :
              'In stock: ' + item.liveStock)) +
          '</span>';
      }
      var unavailable = item.liveAvailable === false
        ? '<span class="mc-quote-unavailable">No longer available</span>'
        : '';
      var maxAttr = (typeof item.liveStock === 'number') ? ' max="' + item.liveStock + '"' : '';
      var price = (typeof item.price === 'number')
        ? '$' + item.price.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '';

      return '' +
        '<div class="mc-quote-item" data-variant-id="' + item.variantId + '">' +
          (item.image ? '<img class="mc-quote-item__img" src="' + item.image + '" alt="" />' : '<div class="mc-quote-item__img mc-quote-item__img--ph"></div>') +
          '<div class="mc-quote-item__body">' +
            '<a class="mc-quote-item__title" href="' + item.url + '">' + escapeHTML(item.title) + '</a>' +
            (item.variantTitle ? '<div class="mc-quote-item__variant">' + escapeHTML(item.variantTitle) + '</div>' : '') +
            '<div class="mc-quote-item__meta">' +
              '<span class="mc-quote-item__price">' + price + '</span>' +
              ((item.qty || 1) > 1 && typeof item.price === 'number'
                ? '<span class="mc-quote-item__subtotal">' +
                    (item.qty || 1) + ' × = ' +
                    '$' + (item.price * (item.qty || 1)).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
                  '</span>'
                : '') +
              stockNote +
              unavailable +
            '</div>' +
            '<div class="mc-quote-item__controls">' +
              '<input type="number" class="mc-quote-qty" min="1"' + maxAttr + ' value="' + item.qty + '" data-variant-id="' + item.variantId + '" />' +
              '<button class="mc-quote-remove" type="button" data-variant-id="' + item.variantId + '" aria-label="Remove">×</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    }).join('');

    // Drawer grand-total: render after items list when items exist
    var grand = items.reduce(function (s, it) {
      return s + ((typeof it.price === 'number' ? it.price : 0) * (it.qty || 1));
    }, 0);
    if (grand > 0) {
      container.innerHTML += '<div class="mc-quote-drawer-total">' +
        '<span>Indicative subtotal</span>' +
        '<strong>$' + grand.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '</strong>' +
      '</div>';
    }
  }

  // ---------- Event wiring ----------
  function bind() {
    // Open/close drawer
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-mc-quote-open]');
      if (openBtn) { e.preventDefault(); open(); return; }

      var closeBtn = e.target.closest('[data-mc-quote-close]');
      if (closeBtn) { e.preventDefault(); close(); return; }

      var addBtn = e.target.closest('[data-mc-quote-add]');
      if (addBtn) {
        e.preventDefault();
        var raw = addBtn.getAttribute('data-mc-quote-add');
        try {
          var item = JSON.parse(raw);
          add(item);
          open();
        } catch (err) {
          console.warn('MCQuote: bad payload on data-mc-quote-add', err);
        }
        return;
      }

      var removeBtn = e.target.closest('.mc-quote-remove');
      if (removeBtn) {
        var rid = parseInt(removeBtn.getAttribute('data-variant-id'), 10);
        remove(rid);
        return;
      }
    });

    // Qty changes
    document.addEventListener('change', function (e) {
      var input = e.target.closest('.mc-quote-qty');
      if (!input) return;
      var vid = parseInt(input.getAttribute('data-variant-id'), 10);
      var qty = parseInt(input.value, 10) || 1;
      updateQty(vid, qty);
    });

    // Esc closes drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  // ---------- Utilities ----------
  function fire(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || null }));
  }
  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  // ---------- Init ----------
  function init() {
    bind();
    render();
    refreshLiveStock();
  }

  // Expose
  window.MCQuote = {
    add: add, remove: remove, updateQty: updateQty,
    clear: clear, get: get, count: count,
    open: open, close: close, toggle: toggle,
    refreshLiveStock: refreshLiveStock
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
